import {
  Flex,
  NativeSelectField,
  NativeSelectRoot,
  Spinner,
} from "@chakra-ui/react"
import { useEffect, useState } from "react"

import {
  useOrganizations,
  useSwitchOrganization,
} from "@/hooks/useOrganizations"
import { toaster } from "../ui/toaster"

const SELECTED_ORG_KEY = "selected_org_id"

function OrganizationSwitcher() {
  const { data: organizations, isLoading } = useOrganizations()
  const switchOrgMutation = useSwitchOrganization()
  const [selectedOrgId, setSelectedOrgId] = useState<string>(
    () => localStorage.getItem(SELECTED_ORG_KEY) || "",
  )

  // Validate stored org against loaded list; fall back to first if invalid
  useEffect(() => {
    if (!organizations || organizations.length === 0) return
    const isValid = organizations.some((org) => org.id === selectedOrgId)
    if (!isValid) {
      setSelectedOrgId(organizations[0].id)
    }
  }, [organizations, selectedOrgId])

  // Only show switcher if user has multiple organizations
  if (isLoading || !organizations || organizations.length <= 1) {
    return null
  }

  const handleOrgChange = (orgId: string) => {
    const previousOrgId = selectedOrgId
    setSelectedOrgId(orgId)
    localStorage.setItem(SELECTED_ORG_KEY, orgId)
    switchOrgMutation.mutate(
      { org_id: orgId },
      {
        onSuccess: () => {
          window.location.reload()
        },
        onError: (error: any) => {
          toaster.create({
            title: "Failed to switch organization",
            description: error.message || "Please try again",
            type: "error",
            duration: 5000,
          })
          // Revert selection to what it was before
          setSelectedOrgId(previousOrgId)
          localStorage.setItem(SELECTED_ORG_KEY, previousOrgId)
        },
      },
    )
  }

  return (
    <Flex direction="column" gap={1} width="100%">
      <Flex align="center" gap={2}>
        <NativeSelectRoot
          size="sm"
          flex={1}
          data-testid="organization-switcher"
        >
          <NativeSelectField
            value={selectedOrgId}
            onChange={(e) => handleOrgChange(e.target.value)}
            aria-label="Organization"
          >
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </NativeSelectField>
        </NativeSelectRoot>
        {switchOrgMutation.isPending && (
          <Spinner size="sm" data-testid="org-switch-pending" />
        )}
      </Flex>
    </Flex>
  )
}

export default OrganizationSwitcher
